/* Built In Imports */
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import Breadcrum from '@components/Navigation/Breadcrum';
import LeftNavCommonUI from '@components/UI/Common/LeftNavCommonUI';
import OthersCommonUI from '@components/UI/Others/OthersCommonUI';
import HeadComponent from '@components/Utility/HeadComponent';
import config from '@config';
import WrapLayout from '@Layout/WrapLayout';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/othersService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Health Landing Page Data
 */
const OthersFirstLevel = ({ region, language, pageData }) => {
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  // consoleLog('page', pageData);

  return (
    <WrapLayout pad={0}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
        pageconfig={pageData?.pageConfig}
      >
        {' '}
      </HeadComponent>
      <Breadcrum
        page={pageData?.breadcrumbs}
        alsoin={pageData?.alsoin}
        url=""
        region={region}
        lang={language}
        pageConfig={pageData?.pageConfig}
      />

      <Box as="section" className="page-wrap container-article" p="0">
        <OthersCommonUI
          pageBody={pageData?.body}
          region={region}
          lang={language}
          pageConfig={pageData?.pageConfig}
        />
        <LeftNavCommonUI
          pageBody={pageData?.bodyLeftNavSections}
          region={region}
          lang={language}
          isLeftMenu
        />
        <OthersCommonUI
          pageBody={pageData?.bodyBottomSection}
          region={region}
          lang={language}
        />
      </Box>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const othersFirstLevelLandingPaths = config.USE_LOCAL_URL
    ? [
        `/in/en/recurring-donations/faq`,
        `/in/en/sanghamitra/donate`,
        `/us/en/sanghamitra/donate`,
      ]
    : await getLandingPaths('L1', '/', [
        '/sadhguru/',
        '/blog/',
        '/center/',
        '/events/',
        '/donation/',
        '/donation-new/',
        '/health/',
        '/inner-engineering',
        '/inner-engineering-pc',
        '/inner-engineering-pc-2',
        '/inner-engineering-migration/',
        '/inner-engineering-online/',
        '/maha-yoga-yagna/',
        '/grace-of-yoga/',
        '/my-account/',
        '/program-details/',
        '/program-embed-register',
        '/program-finder',
        '/quotes/',
        '/rudraksha-diksha/',
        '/sadhguru-exclusive/',
        '/sadhguru-exclusive-free-trial',
        '/subscribe-confirmation/',
        '/subscribe-failure/',
        '/topic/',
        '/volunteer/',
        '/wisdom/',
        '/yoga-meditation/',
        '/redirect-to-site/',
        '/news/',
        '/outreach/',
        '/search/',
        '/youth-n-truth/',
        '/isha-foundation/',
        '/sadhguru-radio/',
        '/sadhguru-app/',
        '/sadhguru-school-uganda/',
        '/project-samskriti/',
        '/mahashivratri/',
        '/sadhguru-academy/',
        '/sacred-walks/',
        '/linga-bhairavi/',
        '/daily-quotes-subscription/',
        '/newsletter-subscription/',
        '/publications/',
        '/sadhguru-gurukulam/',
        '/sadhguru-schedule/',
        '/instagram-link-bio/',
        '/pancha-bhuta-kriya-online-with-sadhguru-mahashivratri/',
        '/pancha-bhuta-kriya/',
        `pancha-bhuta-kriya-v1/`,
        '/official-digital-handles',
        '/corporate-social-responsibility/',
        '/21-day-challenge-idy',
        '/refer-21-day-challenge-idy',
        `/miracle-of-mind`,
        '/register-tr',
      ]);

  // consoleLog('othersFirstLevelLandingPaths', othersFirstLevelLandingPaths);
  return {
    paths: othersFirstLevelLandingPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language, others, firstLevel },
  req,
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  const pageData = await fetchDetails(
    region,
    language,
    `${others}/${firstLevel}`,
    { preview, data: previewData }
  );

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
    },
  };
};

export default OthersFirstLevel;
